/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState, useContext } from 'react';
import { isNil, isEmpty, get } from 'lodash';
import { Form } from 'react-final-form';
import { css, StyleSheet } from 'aphrodite';
import { Text } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { completeTask } from 'blb-api/tasks';
import { MeContext } from 'blb-contexts/Me';
import { errorMessage } from 'blb-utils/errors';
import { TaskModal } from '../../TaskModal';
import { Task } from '../Task';
import { NotaryOptions } from './NotaryOptions';

export namespace SelectRONProviderTask {
  export interface Props extends Omit<BlendBridge.API.BaseTask, 'type'> {
    onSuccess: () => Promise<any>;
    isBlendDisabled: boolean;
  }
  export type FormValues = BlendBridge.API.SelectRONProviderTaskCompleteMeta;
}

const styles = StyleSheet.create({
  ItemRow: {
    marginBottom: 20,
  },
  itemRow: {
    ':not(:last-child)': {
      marginBottom: 24,
    },
  },
});

export const SelectRONProviderTask: React.FC<SelectRONProviderTask.Props> = ({
  buttonLabel,
  disabledReason,
  displayType,
  isDisabled,
  onSuccess,
  taskHeader,
  taskId,
  isBlendDisabled,
}: SelectRONProviderTask.Props) => {
  const me = useContext(MeContext);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [checkedValue, setCheckedValue] = useState<string | null>(null);

  const handleClose = () => {
    setError(undefined);
    setModalOpen(false);
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedValue(event.target.value);
    setError(undefined);
  };

  const handleFormSubmit = async (formValues: SelectRONProviderTask.FormValues) => {
    if (!isNil(taskId)) {
      try {
        setLoading(true);
        // hit the external service to complete the task
        const body: BlendBridge.API.CompleteTaskBody = {};
        if (isEmpty(formValues)) {
          setError('Please select a RON platform.');
          setLoading(false);
          return;
        } else {
          setError(undefined);
          body.meta = formValues;
        }

        await completeTask(taskId, body);
        await onSuccess();
      } catch (err) {
        setError(get(err, 'response.data.error.message', errorMessage('SENDING_FORM', me?.principalType)));
        setLoading(false);
      }
    }
  };

  return (
    <Task
      buttonLabel={buttonLabel}
      disabledReason={disabledReason}
      displayType={displayType}
      isDisabled={isDisabled}
      onClick={() => setModalOpen(true)}
      taskHeader={taskHeader}
      taskId={taskId}
      type={BlendBridge.TaskTypes.SELECT_RON_PROVIDER}
    >
      <Form onSubmit={handleFormSubmit}>
        {({ handleSubmit }) => {
          return (
            <TaskModal
              divideFooter={true}
              error={error}
              handleClose={handleClose}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
              isOpen={isModalOpen}
              submitButtonLabel="Confirm"
              taskId={taskId}
              title={taskHeader}
            >
              <div className={css(styles.itemRow)}>
                <Text fontSize="lg">
                  Please select the RON platform that will be used for this closing appointment.
                </Text>
              </div>
              <NotaryOptions
                checkedValue={checkedValue}
                handleOnChange={handleOnChange}
                handleSubmit={handleSubmit}
                isBlendDisabled={isBlendDisabled}
              />
            </TaskModal>
          );
        }}
      </Form>
    </Task>
  );
};
